import { DatePicker, Modal, Select, Skeleton, Typography } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { getProjectDocumentReviewers } from "../../Project/api/project-documents.services.ts"
import { type FileItem } from "../types/file-item.types.ts"
import { TextArea } from "../../../components/Input/TextArea.tsx"

const { Text, Title } = Typography

interface RequestReviewModalProps {
  file: FileItem
  onClose: () => void
  onSubmit: (data: {
    reviewerId: string
    dueDate: string | null
    message: string | null
  }) => Promise<void>
}

export function RequestReviewModal({
  file,
  onClose,
  onSubmit
}: RequestReviewModalProps) {
  const { t } = useTranslation()
  const [reviewers, setReviewers] = useState<
    { label: string; value: string }[]
  >([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [reviewerId, setReviewerId] = useState<string | null>(null)
  const [dueDate, setDueDate] = useState<Dayjs | null>(null)
  const [message, setMessage] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    void getProjectDocumentReviewers(file.id).then((response) => {
      if (cancelled) return
      setLoading(false)
      if (!response.ok || !response.data) {
        setLoadFailed(true)
        return
      }
      setReviewers(response.data)
    })
    return () => {
      cancelled = true
    }
  }, [file.id])

  const handleSubmit = async () => {
    if (!reviewerId) return
    setSaving(true)
    await onSubmit({
      reviewerId,
      dueDate: dueDate ? dueDate.format("YYYY-MM-DD") : null,
      message: message.trim() || null
    })
    setSaving(false)
  }

  return (
    <Modal
      open
      title={t("files.review.requestTitle")}
      onCancel={onClose}
      okText={t("files.review.submit")}
      okButtonProps={{ disabled: !reviewerId, loading: saving }}
      onOk={() => void handleSubmit()}
      cancelButtonProps={{ disabled: saving }}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <Text type="secondary">{t("files.review.document")}</Text>
          <Title level={5} className="!mb-0 break-all">
            {file.name}
          </Title>
        </div>

        <div className="flex flex-col gap-1">
          <Text type="secondary">{t("files.review.version")}</Text>
          <Text>
            {file.version !== undefined
              ? t("fileViewer.detailsPanel.versionShort", {
                  version: file.version
                })
              : "—"}
          </Text>
        </div>

        <div className="flex flex-col gap-2">
          <Text type="secondary">{t("files.review.reviewer")}</Text>
          {loading ? (
            <Skeleton.Input active block />
          ) : (
            <Select<string>
              showSearch
              disabled={saving}
              value={reviewerId ?? undefined}
              onChange={(value) => setReviewerId(value)}
              options={reviewers}
              optionFilterProp="label"
              placeholder={t("files.review.reviewerPlaceholder")}
              notFoundContent={
                loadFailed
                  ? t("files.review.reviewersLoadFailed")
                  : t("files.review.noEligibleReviewers")
              }
              className="w-full"
            />
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Text type="secondary">{t("files.review.dueDate")}</Text>
          <DatePicker
            disabled={saving}
            value={dueDate}
            onChange={setDueDate}
            disabledDate={(date) => date.isBefore(dayjs().startOf("day"))}
            className="w-full"
          />
        </div>

        <div className="flex flex-col gap-2">
          <Text type="secondary">{t("files.review.message")}</Text>
          <TextArea
            disabled={saving}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("files.review.messagePlaceholder")}
            rows={3}
            maxLength={1000}
          />
        </div>
      </div>
    </Modal>
  )
}
